import { useState } from "react";
import { formatInstant, instantTitle } from "../ui/instant.js";

/**
 * DeliveryLog
 *
 * `time` arrives as an RFC 3339 UTC instant — the only sane wire format — and is
 * rendered in the reader's timezone, because a log is read against the reader's
 * own clock, not against UTC. The exact instant the server recorded stays in the
 * cell's `title`, which is what you want when comparing a row with a server log.
 *
 * Props:
 *   project — { name, callbackUrl }
 *   deliveries — [{ id, time, feedExternalId, attempts, maxAttempts, status: 'ok'|'retrying'|'failed' }]
 *   activeRetry — optional { feedExternalId, events: [{label, elapsed, state:'done'|'active'|'pending'}], nextAttemptLabel, attempt, maxAttempts }
 *   payloadPreview — object, shown pretty-printed
 *   onFilterChange(status)
 *   onOpenDelivery(id)
 *   timeZone — IANA zone for the timestamps; defaults to the browser's. A prop
 *              only so tests can assert a conversion deterministically.
 */
export default function DeliveryLog({
  project,
  deliveries = [],
  activeRetry,
  payloadPreview,
  onFilterChange,
  onOpenDelivery,
  timeZone,
}) {
  const [filter, setFilter] = useState("all");

  function statusBadge(status) {
    if (status === "ok") return { cls: "ical-badge-success", label: "200 OK" };
    if (status === "retrying") return { cls: "ical-badge-warning", label: "Retrying" };
    return { cls: "ical-badge-danger", label: "Failed" };
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-lg)" }}>Delivery log</p>
          <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>{project?.name} · {project?.callbackUrl}</p>
        </div>
        <select
          className="ical-select"
          style={{ width: 140 }}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            onFilterChange?.(e.target.value);
          }}
        >
          <option value="all">All statuses</option>
          <option value="failed">Failed</option>
          <option value="retrying">Retrying</option>
        </select>
      </div>

      <div className="ical-delivery-head" style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
        {/* The header says whose clock the column is on: "10:15" in a log is
            otherwise assumed to be UTC by anyone who has been debugging one. */}
        <span>Time (your time zone)</span><span>Feed</span><span>Attempts</span><span>Status</span><span />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {deliveries.map((d) => {
          const badge = statusBadge(d.status);
          return (
            <div
              key={d.id}
              className="ical-row"
              className="ical-delivery-row"
              style={{ cursor: "pointer" }}
              onClick={() => onOpenDelivery?.(d.id)}
            >
              <span
                style={{ fontSize: "var(--text-sm)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}
                title={instantTitle(d.time, { timeZone })}
              >
                {formatInstant(d.time, { timeZone })}
              </span>
              <span className="ical-delivery-feed" style={{ fontSize: "var(--text-sm)" }}>{d.feedExternalId}</span>
              <span style={{ fontSize: "var(--text-sm)" }}>{d.attempts} / {d.maxAttempts}</span>
              <span className={`ical-badge ical-delivery-status ${badge.cls}`}>{badge.label}</span>
              <span className="ical-delivery-chevron" style={{ color: "var(--text-muted)" }}>›</span>
            </div>
          );
        })}
      </div>

      {activeRetry && (
        <div className="ical-card" style={{ marginTop: 20 }}>
          <p style={{ fontSize: "var(--text-sm)", fontWeight: 500, margin: "0 0 8px" }}>Retry timeline — {activeRetry.feedExternalId}</p>
          {/*
            The row scrolls inside the card rather than pushing out of it. A
            timeline can be up to sixteen attempts long, and the offset labels
            ("47h50m43s") are text: flex will not shrink an item below its
            min-content, so the row grew wider than the card and the last dots
            escaped the frame. Scrolling keeps every attempt reachable, which
            truncating them would not — and it is the same treatment the payload
            preview below already uses.
          */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 4 }}>
            {activeRetry.events.map((ev, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", flex: i < activeRetry.events.length - 1 ? 1 : "none", gap: 6 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div
                    style={{
                      width: ev.state === "pending" ? 8 : 10,
                      height: ev.state === "pending" ? 8 : 10,
                      borderRadius: "50%",
                      background:
                        ev.state === "done" ? "var(--fill-danger)" : ev.state === "active" ? "var(--fill-warning)" : "var(--border)",
                    }}
                  />
                  {/* nowrap, or a narrow card would wrap "47h50m43s" onto two
                      lines and break the axis it labels. */}
                  <span style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    {ev.elapsed}
                  </span>
                </div>
                {i < activeRetry.events.length - 1 && <div style={{ flex: 1, height: 1, background: "var(--border)" }} />}
              </div>
            ))}
          </div>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: 0 }}>
            Fibonacci backoff — attempt {activeRetry.attempt} of {activeRetry.maxAttempts} scheduled for {activeRetry.nextAttemptLabel}
          </p>
        </div>
      )}

      {payloadPreview && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: "var(--text-sm)", fontWeight: 500, margin: "0 0 6px" }}>Payload preview</p>
          <pre
            style={{
              background: "var(--surface-1)",
              borderRadius: "var(--radius)",
              padding: 12,
              fontSize: "var(--text-xs)",
              fontFamily: "var(--font-mono)",
              overflowX: "auto",
              margin: 0,
            }}
          >
            {JSON.stringify(payloadPreview, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
